import React, { useState } from 'react';
import { AlertTriangleIcon, TruckIcon } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { MetricsDateFilter } from '../../components/admin/MetricsDateFilter';
import { ExpressCouriersSection } from '../../components/admin/express/ExpressCouriersSection';
import { ExpressAssignSection } from '../../components/admin/express/ExpressAssignSection';
import { ExpressPayoutReport } from '../../components/admin/express/ExpressPayoutReport';
import { Skeleton } from '../../components/ui/Skeleton';
import { useTenantSlug } from '../../lib/use-tenant-slug';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useTenant } from '../../lib/tenant-context';
import { SETUP_PENDING_MESSAGE, canSeeExpressMoney, todayCrIso, useExpressCouriers, useExpressDeliveries } from '../../lib/express-hooks';

// ──────────────────────────────────────────────────────────────────
// Servicio Express — Emilius Pizza (id 27).
//
// Tres secciones en una pantalla: catálogo de repartidores, asignación
// de las entregas del periodo, y reporte de cuánto se le debe a cada
// uno. Todo el rango de fechas trabaja en hora de Costa Rica.
// ──────────────────────────────────────────────────────────────────

export function ServicioExpressPage() {
  const slug = useTenantSlug();
  const tenantCtx = useTenant();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const [startDate, setStartDate] = useState(todayCrIso);
  const [endDate, setEndDate] = useState(todayCrIso);
  const isEmilius = String(restaurant?.id) === '27' || restaurant?.slug === 'emilius-pizza';
  const restaurantId = isEmilius ? restaurant?.id : undefined;
  // Solo administración ve dinero. El resto de roles opera igual
  // (asigna repartidores) pero sin montos ni reporte de pagos.
  const canSeeMoney = canSeeExpressMoney(tenantCtx?.role);
  const {
    couriers,
    activeCouriers,
    loading: couriersLoading,
    setupPending: couriersSetupPending,
    error: couriersError,
    refetch: refetchCouriers
  } = useExpressCouriers(restaurantId);
  const {
    rows,
    loading: deliveriesLoading,
    error: deliveriesError,
    setupPending: deliveriesSetupPending,
    refetch: refetchDeliveries
  } = useExpressDeliveries(restaurantId, startDate, endDate);
  const setupPending = couriersSetupPending || deliveriesSetupPending;
  if (restLoading || !restaurant) {
    return <div className="min-h-screen w-full bg-gray-50 flex items-center justify-center" data-id="element-6387" data-mp="3qfe822ao7f1j">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-6388" data-mp="3vcmlxqoscwwz" />
      </div>;
  }
  if (!isEmilius) {
    return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-6389" data-mp="3ggf5n1gi0fmz">
        <div className="p-6 flex flex-col items-center justify-center min-h-[50vh] text-center" data-id="element-6390" data-mp="251i2yore5rx5">
          <TruckIcon className="w-16 h-16 text-gray-300 mb-4" data-id="element-6391" data-mp="2a0l96cj3y46p" />
          <h1 className="text-xl font-bold text-gray-900 mb-2" data-id="element-6392" data-mp="2a2625xprxf2g">
            Módulo no disponible
          </h1>
          <p className="text-gray-500" data-id="element-6393" data-mp="s1574lui0jwo">
            Esta sección es exclusiva para Emilius Pizza.
          </p>
        </div>
      </AdminLayout>;
  }
  const refreshAll = () => {
    refetchCouriers();
    refetchDeliveries();
  };
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-6394" data-mp="x1jf63w3ntup">
      <main className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4 w-full" data-id="element-6395" data-mp="9djn90n1xxej">
        <header data-id="element-6396" data-mp="4dnkf4q3cc0k">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-6397" data-mp="4e6szutupo1x">Servicio Express</h1>
          <p className="text-sm text-gray-500" data-id="element-6398" data-mp="i4k0xx6qt2r9">
            {canSeeMoney ? 'Repartidores, asignación de entregas y pagos · Emilius Pizza' : 'Repartidores y asignación de entregas · Emilius Pizza'}
          </p>
        </header>

        {setupPending && <div role="alert" className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900 flex items-start gap-3" data-id="element-6399" data-mp="3hsa4ok9wq04l">
            <AlertTriangleIcon className="w-5 h-5 shrink-0 mt-0.5" data-id="element-6400" data-mp="3ujcazvztyq67" />
            <div data-id="element-6401" data-mp="3c9jt4jzsjvz">
              <p className="font-bold" data-id="element-6402" data-mp="3brku05y3rkd">Falta habilitar el módulo</p>
              <p className="mt-0.5" data-id="element-6403" data-mp="8a1axwctc1z4">{SETUP_PENDING_MESSAGE}</p>
            </div>
          </div>}

        {(couriersError || deliveriesError) && <div role="alert" className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-id="element-6404" data-mp="d2ujywfbdwh0">
            {couriersError || deliveriesError}
          </div>}

        <MetricsDateFilter startDate={startDate} endDate={endDate} onChange={(s, e) => {
        setStartDate(s);
        setEndDate(e);
      }} data-id="element-6405" data-mp="r12b2npo63k9" />

        <ExpressCouriersSection restaurantId={restaurant.id} couriers={couriers} loading={couriersLoading} onChanged={refreshAll} data-id="element-6406" data-mp="2ssfdn645zzd5" />

        <ExpressAssignSection restaurantId={restaurant.id} rows={rows} activeCouriers={activeCouriers} loading={deliveriesLoading} canSeeMoney={canSeeMoney} onChanged={refreshAll} data-id="element-6407" data-mp="2xt3eeji73ukk" />

        {canSeeMoney && <ExpressPayoutReport restaurantId={restaurant.id} rows={rows} loading={deliveriesLoading} startDate={startDate} endDate={endDate} onChanged={refreshAll} data-id="element-6408" data-mp="2gi7me8v0uuiq" />}
      </main>
    </AdminLayout>;
}